import React, { useMemo, useState } from 'react';
import { useSubscription } from '../../contexts/SubscriptionContext.jsx';
import { setDismissed, isDismissed } from '../../utils/bannerPrefs.js';

export function NotificationSubscriptionBanner() {
    const { permission, isSubscribed, isInitialized, isLoading, requestPermissionAndSubscribe } = useSubscription();
    const [locallyDismissed, setLocallyDismissed] = useState(false);

    const { shouldShow, reason } = useMemo(() => {
        if (!isInitialized || isLoading) return { shouldShow: false, reason: '' };
        if (permission === 'default') return { shouldShow: true, reason: 'permission_needed' };
        if (permission === 'granted' && !isSubscribed) return { shouldShow: true, reason: 'subscription_missing' };
        return { shouldShow: false, reason: '' };
    }, [permission, isSubscribed, isInitialized, isLoading]);

    const dismissedKey = `subscription-${reason}`;
    if (!shouldShow || locallyDismissed || (reason && isDismissed(dismissedKey))) return null;

    const color = reason === 'subscription_missing' ? 'orange' : 'blue';

    const onEnable = async () => {
        try {
            await requestPermissionAndSubscribe();
            setLocallyDismissed(true);
        } catch { }
    };

    const onDismiss = () => {
        setLocallyDismissed(true);
        setDismissed(dismissedKey, 24);
    };

    return (
        <div className={`mb-4 border-l-4 p-4 ${color === 'orange' ? 'border-orange-400 bg-orange-50 text-orange-700' : 'border-blue-400 bg-blue-50 text-blue-700'}`}>
            <div className="flex">
                <div className="flex-shrink-0">
                    <svg className={`h-5 w-5 ${color === 'orange' ? 'text-orange-400' : 'text-blue-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {color === 'orange' ? (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        ) : (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-4 4-4-4h3v-3a7 7 0 01-14 0v3h3l-4 4-4-4h5a9 9 0 0118 0z" />
                        )}
                    </svg>
                </div>
                <div className="ml-3 flex-1">
                    <p className="text-sm font-medium">
                        {reason === 'subscription_missing' ? 'Notifications Not Active' : 'Enable Notifications'}
                    </p>
                    <p className="mt-1 text-sm">
                        {reason === 'subscription_missing'
                            ? 'Permission is granted but subscription is missing. Please enable notifications.'
                            : 'Stay updated with training reminders and important announcements.'}
                    </p>
                </div>
                <div className="ml-3 flex flex-shrink-0 gap-2">
                    <button onClick={onEnable} disabled={isLoading} className={`rounded px-3 py-1 text-xs font-medium text-white transition-colors duration-200 ${color === 'orange' ? 'bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300' : 'bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300'}`}>
                        {isLoading ? 'Setting up...' : 'Enable'}
                    </button>
                    <button onClick={onDismiss} disabled={isLoading} className={`px-2 py-1 text-xs font-medium ${color === 'orange' ? 'text-orange-600 hover:text-orange-800' : 'text-blue-600 hover:text-blue-800'}`}>
                        Dismiss
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function SubscriptionBannerHost() {
    return (
        <div className="px-4">
            <NotificationSubscriptionBanner />
        </div>
    );
}


